// @ts-nocheck
/**
 * SEO Layout Component
 * Carbon Design System compliant layout with SEO optimization
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Header,
  HeaderContainer,
  HeaderName,
  HeaderNavigation,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderGlobalBar,
  HeaderGlobalAction,
  SkipToContent,
  SideNav,
  SideNavItems,
  SideNavMenuItem,
  Content,
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  Loading,
} from '@carbon/react';
import { Search, Notification, UserAvatar, Language, Earth } from '@carbon/react/icons';
import { SEOUtils, SEOPageData } from '../utils/seo-utils';

interface SEOLayoutProps {
  children: React.ReactNode;
  page: string;
  language?: string;
  breadcrumbs?: Array<{ href: string; text: string }>;
  showNavigation?: boolean;
  className?: string;
}

export const SEOLayout: React.FC<SEOLayoutProps> = ({
  children,
  page,
  language = 'en',
  breadcrumbs = [],
  showNavigation = true,
  className = '',
}) => {
  const [seoData, setSeoData] = useState<SEOPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSideNavExpanded, setIsSideNavExpanded] = useState(false);

  const seoUtils = SEOUtils.getInstance();

  useEffect(() => {
    const loadSEOData = async () => {
      setIsLoading(true);
      try {
        const data = await seoUtils.loadPageSEO(page, language);
        if (data) {
          setSeoData(data);
          seoUtils.updateDocumentHead(data);
          seoUtils.generateHreflangTags(page);
        }
      } catch (error) {
        console.error('Failed to load SEO data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSEOData();

    // Optimize images and track performance
    const timer = setTimeout(() => {
      seoUtils.optimizeImages();
      seoUtils.trackCoreWebVitals();
    }, 100);

    return () => clearTimeout(timer);
  }, [page, language, seoUtils]);

  useEffect(() => {
    // Preload critical resources
    seoUtils.preloadCriticalResources();
  }, [seoUtils]);

  const navigationItems = [
    { href: '/', text: language === 'pt' ? 'Início' : 'Home' },
    { href: '/features', text: language === 'pt' ? 'Recursos' : 'Features' },
    { href: '/pricing', text: language === 'pt' ? 'Preços' : 'Pricing' },
    { href: '/success-stories', text: language === 'pt' ? 'Histórias de Sucesso' : 'Success Stories' },
    { href: '/demo-request', text: language === 'pt' ? 'Solicitar Demo' : 'Request Demo' },
  ];

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalUrl = seoData?.meta.canonical || currentUrl;

  if (isLoading) {
    return (
      <div className="seo-layout--loading">
        <Loading description={language === 'pt' ? 'Carregando...' : 'Loading...'} />
      </div>
    );
  }

  return (
    <>
      {seoData && (
        <Helmet>
          <title>{seoData.meta.title}</title>
          <meta name="description" content={seoData.meta.description} />
          <meta name="keywords" content={seoData.meta.keywords.join(', ')} />
          <meta name="robots" content={seoData.meta.robots || 'index,follow'} />
          <meta name="viewport" content={seoData.meta.viewport || 'width=device-width,initial-scale=1'} />
          <meta name="theme-color" content={seoData.meta.themeColor || '#0f62fe'} />
          <link rel="canonical" href={canonicalUrl} />

          {/* OpenGraph tags */}
          {Object.entries(seoData.openGraph).map(([property, content]) => (
            <meta key={property} property={property} content={content} />
          ))}

          {/* Twitter Card tags */}
          {Object.entries(seoData.twitter).map(([name, content]) => (
            <meta key={name} name={name} content={content} />
          ))}

          {/* Structured Data */}
          {seoData.structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))}

          {/* Language alternatives */}
          <link rel="alternate" hrefLang="en" href={`${window.location.origin}/${page}`} />
          <link rel="alternate" hrefLang="pt-BR" href={`${window.location.origin}/pt/${page}`} />
          <link rel="alternate" hrefLang="x-default" href={`${window.location.origin}/${page}`} />

          {/* DNS prefetch for performance */}
          <link rel="dns-prefetch" href="//www.google-analytics.com" />
        </Helmet>
      )}

      <div className={`seo-layout ${className}`}>
        {showNavigation && (
          <HeaderContainer
            render={({ isSideNavExpanded, onClickSideNavExpand }) => (
              <>
                <Header aria-label="Sigma08 Educational Platform">
                  <SkipToContent />
                  <HeaderMenuButton
                    aria-label={language === 'pt' ? 'Abrir menu' : 'Open menu'}
                    onClick={onClickSideNavExpand}
                    isActive={isSideNavExpanded}
                  />
                  <HeaderName href="/" prefix="Sigma08">
                    {language === 'pt' ? 'Plataforma Educacional' : 'Educational Platform'}
                  </HeaderName>

                  <HeaderNavigation aria-label="Sigma08 Navigation">
                    {navigationItems.map(item => (
                      <HeaderMenuItem
                        key={item.href}
                        href={language === 'pt' && item.href !== '/' ? `/pt${item.href}` : item.href}
                      >
                        {item.text}
                      </HeaderMenuItem>
                    ))}
                  </HeaderNavigation>

                  <HeaderGlobalBar>
                    <HeaderGlobalAction
                      aria-label={language === 'pt' ? 'Pesquisar' : 'Search'}
                      tooltipAlignment="center"
                    >
                      <Search size={20} />
                    </HeaderGlobalAction>
                    <HeaderGlobalAction
                      aria-label={language === 'pt' ? 'Notificações' : 'Notifications'}
                      tooltipAlignment="center"
                    >
                      <Notification size={20} />
                    </HeaderGlobalAction>
                    <HeaderGlobalAction
                      aria-label={language === 'pt' ? 'Idioma' : 'Language'}
                      tooltipAlignment="end"
                      href={language === 'pt' ? `/${page}` : `/pt/${page}`}
                    >
                      <Language size={20} />
                    </HeaderGlobalAction>
                    <HeaderGlobalAction
                      aria-label={language === 'pt' ? 'Perfil do usuário' : 'User Profile'}
                      tooltipAlignment="end"
                    >
                      <UserAvatar size={20} />
                    </HeaderGlobalAction>
                  </HeaderGlobalBar>

                  <SideNav aria-label="Side navigation" expanded={isSideNavExpanded} isPersistent={false}>
                    <SideNavItems>
                      {navigationItems.map(item => (
                        <SideNavMenuItem
                          key={item.href}
                          href={language === 'pt' && item.href !== '/' ? `/pt${item.href}` : item.href}
                        >
                          {item.text}
                        </SideNavMenuItem>
                      ))}
                    </SideNavItems>
                  </SideNav>
                </Header>
              </>
            )}
          />
        )}

        <Content>
          <Grid className="seo-layout__content" fullWidth>
            {breadcrumbs.length > 0 && (
              <Column lg={16} md={8} sm={4}>
                <Breadcrumb noTrailingSlash aria-label="Page navigation">
                  {breadcrumbs.map((crumb, index) => (
                    <BreadcrumbItem key={index} href={crumb.href} isCurrentPage={index === breadcrumbs.length - 1}>
                      {crumb.text}
                    </BreadcrumbItem>
                  ))}
                </Breadcrumb>
              </Column>
            )}

            <Column lg={16} md={8} sm={4}>
              <main role="main" className="seo-layout__main" id="main-content">
                {children}
              </main>
            </Column>
          </Grid>
        </Content>

        {/* Schema.org WebSite markup for search */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Sigma08',
            url: 'https://sigma08.com',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://sigma08.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          })}
        </script>
      </div>
    </>
  );
};

export default SEOLayout;
